import { useContext, useState } from 'react';

import { fetchPostSellerPayLink } from '@iwoca/lapi-client/iwocapay';
import { AlertBox, Button } from '@iwoca/orion';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';

import styles from './OneTimeLinksPage.module.css';
import { ActivationContext } from '../../../activation/Activation';
import {
  TPricingTypes,
  TPricingType,
} from '../../../Buyer/PayLinkLanding/utils/PayLinkLanding.types';
import { Input } from '../../../components/Input/Input';
import { LockedButton } from '../../../components/LockedButton/LockedButton';
import { PayMeLink } from '../../../components/PayMeLink/PayMeLink';
import { useSellerHandle } from '../../../hooks/useSellerHandle.hook';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import { getPayLinkUrl } from '../../../utils/getPayLinkUrl';
import { CheckoutPreview } from '../components/CheckoutPreview/CheckoutPreview';
import { PayLinkPricingFormField } from '../components/PayLinkPricingFormField/PayLinkPricingFormField';
import {
  Column,
  VerticalSplitLayout,
} from '../components/VerticalSplitLayout/VerticalSplitLayout';
import { usePayIn12 } from '../hooks/usePayIn12';
import payLinksPageStyles from '../PayLinks.module.css';
import { DEFAULT_12M_PRICING_OPTION } from '../PayLinksNavigation';
import { usePreview } from '../usePreview';

export const OneTimeLinksPage = () => {
  const { sellerHandle } = useSellerHandle();
  const { previewValues, setPreviewValuesFromEvent, isModularPricing } =
    usePreview();

  if (!sellerHandle) return null;

  return (
    <VerticalSplitLayout className={payLinksPageStyles.PayLinksPage}>
      <Column>
        <div className={payLinksPageStyles.leftSide}>
          <h1 className={payLinksPageStyles.title}>
            A custom pay link for a specific invoice
          </h1>
          <p className={payLinksPageStyles.description}>
            You set the amount, reference and terms tailored to your customer{' '}
            <br />- <b>it can only be used once.</b>
          </p>

          {previewValues?.pricing_3m ? (
            <CreateOneTimePayLinkForm
              onChange={setPreviewValuesFromEvent}
              pricingTypes={{
                pricing_3m: previewValues.pricing_3m?.type as TPricingType,
                pricing_12m: previewValues.pricing_12m?.type as TPricingType,
              }}
              isModularPricing={isModularPricing}
            />
          ) : null}
        </div>
      </Column>
      <Column>
        {!!previewValues.pricing_3m ? (
          <CheckoutPreview
            sellerName={previewValues.sellerName}
            amount={previewValues.amount}
            reference={previewValues.reference}
            pricingPromotions={{
              THREE_MONTHS: previewValues.pricing_3m.promotions,
              TWELVE_MONTHS: previewValues.pricing_12m?.promotions,
            }}
            representativeInterest={{
              THREE_MONTHS: previewValues.pricing_3m.representativeInterest,
              TWELVE_MONTHS: previewValues.pricing_12m?.representativeInterest,
            }}
          />
        ) : (
          <div>Loading preview...</div>
        )}
      </Column>
    </VerticalSplitLayout>
  );
};

const ERROR_MESSAGE =
  'Error creating pay link. Please refresh the page and try again.';

const CreateOneTimePayLinkForm = ({
  onChange,
  pricingTypes,
  isModularPricing,
}: {
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  pricingTypes: TPricingTypes;
  isModularPricing: boolean;
}) => {
  const [payLink, setPayLink] = useState<string | null>(null);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const { stateKey } = useStateKey();
  const { payIn12Enabled } = usePayIn12();
  const INITIAL_VALUES = {
    amount: '',
    reference: '',
    pricing_3m: pricingTypes.pricing_3m,
    pricing_12m: pricingTypes.pricing_12m || DEFAULT_12M_PRICING_OPTION,
  };

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={Yup.object().shape({
        amount: Yup.number().required('Please provide a pay link amount'),
        reference: Yup.string().required('Please provide a pay link reference'),
        pricing_3m: Yup.string().required('Please provide a pay link pricing'),
      })}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true);
        setHasSubmitError(false);
        try {
          const { seller_name, id } = await fetchPostSellerPayLink({
            stateKey: stateKey!,
            body: {
              reference: values.reference,
              amount: parseFloat(values.amount),
              product_pricing: {
                THREE_MONTHS: values.pricing_3m,
                ...(payIn12Enabled && {
                  TWELVE_MONTHS: values.pricing_12m,
                }),
              },
            },
          });

          const payLinkUrl = getPayLinkUrl(seller_name!, id!);
          setPayLink(payLinkUrl);
        } catch (e) {
          setHasSubmitError(true);
        }
        actions.setSubmitting(false);
      }}
      onReset={() => {
        setPayLink(null);
      }}
    >
      {({
        resetForm,
        values,
        handleChange,
      }: FormikProps<typeof INITIAL_VALUES>) => {
        return (
          <Form>
            {hasSubmitError ? (
              <AlertBox variant="failure" className={styles.submitError}>
                {ERROR_MESSAGE}
              </AlertBox>
            ) : null}
            <div className={styles.payLinkConfiguration}>
              <Input
                label="Order Amount"
                name="amount"
                type="number"
                onChange={onChange}
                placeholder="Amount"
                className={styles.configurationInput}
              />
              <Input
                label="Invoice Reference"
                name="reference"
                onChange={onChange}
                placeholder="Reference"
                className={styles.configurationInput}
              />
            </div>
            {isModularPricing ? (
              <PayLinkPricingFormField
                productPricing={{
                  pricing_3m: values.pricing_3m,
                  pricing_12m: values.pricing_12m,
                }}
                onChange={(e) => {
                  onChange(e);
                  handleChange(e);
                }}
              />
            ) : null}
            {payLink === null ? (
              <div className={payLinksPageStyles.buttonWrapper}>
                <OneTimeLinkButton />
              </div>
            ) : (
              <div className={payLinksPageStyles.payLinkContainer}>
                <PayMeLink payMeUrl={payLink} />
                <div
                  className={payLinksPageStyles.createNewLinkButtonContainer}
                >
                  <Button
                    variant="primary"
                    className={styles.linkButton}
                    onClick={() => resetForm()}
                  >
                    Create a new one-time link
                  </Button>
                </div>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

function OneTimeLinkButton() {
  const { isOnboarded, isLoading } = useContext(ActivationContext);

  if (isLoading) return null;

  return (
    <>
      {isOnboarded ? (
        <Button variant="primary" type="submit" className={styles.linkButton}>
          Send a one-time pay link
        </Button>
      ) : (
        <LockedButton />
      )}
    </>
  );
}
